export const initTabLists = () => {
	const els = document.querySelectorAll('.js-tab-list');

	for (const el of els) {
		createTabList(el);
	}
};

/**
 * A tab within a tab list.
 * @typedef {{
 *   el: HTMLElement;
 *   contentEl: HTMLElement;
 *   btnEls: HTMLElement[];
 * }} Tab
 */

/**
 * A tab with a click event listener for each tab button.
 * @typedef {{
*   tab: Tab;
*   handler: EventListener;
* }} TabButtonListener
*/

/**
 * Creates a tab list component.
 * @param {HTMLElement} el - Tab list element.
 */
const createTabList = (el) => {
	/**
	 * Array of all tabs.
	 * @type {Tab[]}
	 */
	const tabs = getTabs(el);

	/**
	 * The active tab.
	 * @type {Tab | null}
	 */
	let activeTab = null;

	/**
	 * Sets the active tab.
	 * @param {Tab | null} tab
	 */
	const setActiveTab = (tab) => {
		if (tab === activeTab) {
			return;
		}

		if (activeTab) {
			activeTab.el.dataset.tabActive = 'false';
			activeTab.el.dataset.menuPositionBarActive = 'false';
			activeTab.contentEl.dataset.tabActive = 'false';
		}

		if (tab) {
			tab.el.dataset.tabActive = 'true';
			tab.el.dataset.menuPositionBarActive = 'true';
			tab.contentEl.dataset.tabActive = 'true';
		}

		activeTab = tab;
	};

	const listeners = createButtonListeners(tabs, setActiveTab);

	/**
	 * Attaches event listeners to the tab list.
	 */
	const listen = () => {
		for (const listener of listeners) {
			for (const btnEl of listener.tab.btnEls) {
				btnEl.addEventListener('click', listener.handler);
			}
		}
	};

	setActiveTab(tabs[0] ?? null);

	listen();
};

/**
 * Returns the tabs within a tab list.
 * @param {HTMLElement} tabListEl - Tab list element.
 * @returns {Tab[]} Tabs.
 */
const getTabs = (tabListEl) => {
	return Array
		.from(tabListEl.querySelectorAll('[data-tab]'))
		.flatMap((el) => {
			const name = el.dataset.tab;

			const contentEl = tabListEl.querySelector(`[data-tab-content="${name}"]`);

			if (!contentEl) {
				console.warn(`Missing tab list content item element for tab "${name}"`);
				return [];
			}

			const btnEls = Array
				.from(tabListEl.querySelectorAll(`[data-tab-btn="${name}"]`));

			if (!btnEls.length) {
				console.warn(`No tab list buttons are set for tab "${name}"`);
			}

			return { el, contentEl, btnEls };
		});
};

/**
 * Creates and returns an array of click listeners for each tab button.
 * @param {Tab[]} tabs - Tabs.
 * @param {(tab: Tab) => void} callback - Callback.
 * @returns {TabButtonListener[]} Tab buttons with created event listeners.
 */
const createButtonListeners = (tabs, callback) => {
	return tabs.map((tab) => {
		const handler = () => {
			callback(tab);
		};

		return { tab, handler };
	});
};
